import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Flex } from "@radix-ui/themes";
import Lowlight from "react-lowlight";
import styled from "@emotion/styled";
import { atlasRoutes } from "@/app/router/routes";

export const CapturedAudioViewPage: FC = () => {
  const navigate = useNavigate();
  const { capturedAudioId } = useParams<{ capturedAudioId: Uuid }>();
  const [isWorking, setIsWorking] = useState(false);

  if (!capturedAudioId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["captured-audio-by-id", capturedAudioId],
    queryFn: () =>
      api.get("/internal/v1/captured-audio/{captured_audio_id}", {
        params: {
          path: {
            captured_audio_id: capturedAudioId,
          },
        },
      }),
    staleTime: 60 * 1000,
  });

  const refresh = () => result.refetch();

  const handleTranscribe = async () => {
    const ok = window.confirm(
      "Generate transcript for this audio? This may consume 3rd-party resources."
    );
    if (!ok) return;
    setIsWorking(true);
    try {
      await api.post("/internal/v1/captured-audio/{captured_audio_id}/transcribe", {
        params: { path: { captured_audio_id: capturedAudioId } },
      });
      alert("Transcription started/succeeded.");
      refresh();
    } catch (err) {
      alert(`Transcription failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsWorking(false);
    }
  };

  const handleConvertMp3 = async () => {
    const ok = window.confirm(
      "Convert original audio to MP3 now? This may take time and overwrite existing MP3."
    );
    if (!ok) return;
    setIsWorking(true);
    try {
      await api.post("/internal/v1/captured-audio/{captured_audio_id}/convert-mp3", {
        params: { path: { captured_audio_id: capturedAudioId } },
      });
      alert("Convert to MP3 requested.");
    } catch (err) {
      alert(`Convert failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsWorking(false);
    }
  };

  const handleDownloadOriginal = async () => {
    try {
      const resp = await api.get("/internal/v1/captured-audio/{captured_audio_id}/signed-urls", {
        params: { path: { captured_audio_id: capturedAudioId } },
      });
      const url = resp.data?.original_download_url;
      if (url) {
        window.open(url, "_blank", "noopener");
      } else {
        alert("Original file is not available.");
      }
    } catch (err) {
      alert(`Failed to download: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDownloadMp3 = async () => {
    try {
      const resp = await api.get("/internal/v1/captured-audio/{captured_audio_id}/signed-urls", {
        params: { path: { captured_audio_id: capturedAudioId } },
      });
      const url = resp.data?.mp3_download_url;
      if (url) {
        window.open(url, "_blank", "noopener");
      } else {
        alert("MP3 is not available.");
      }
    } catch (err) {
      alert(`Failed to download: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleViewAssociatedSession = () => {
    const associatedSessionId = result.data?.data?.associated_session_id;
    if (!associatedSessionId) {
      alert("No associated session.");
      return;
    }

    navigate(atlasRoutes.sessionEvents({ params: { sessionId: associatedSessionId } }).path);
  };

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError || !result.data) {
    return <div>Error: {result.error?.message ?? "Unknown error"}</div>;
  }

  return (
    <Container>
      <br />
      <ButtonGroup>
        <Button variant="outline" disabled={isWorking} onClick={handleTranscribe}>
          {isWorking ? "Working..." : "Transcribe"}
        </Button>
        <Button variant="outline" disabled={isWorking} onClick={handleConvertMp3}>
          {isWorking ? "Working..." : "Convert to MP3"}
        </Button>
        <Button variant="soft" onClick={handleViewAssociatedSession}>
          View Associated Session
        </Button>
        <Button variant="soft" onClick={handleDownloadOriginal}>
          Download Original
        </Button>
        <Button variant="soft" onClick={handleDownloadMp3}>
          Download MP3
        </Button>
      </ButtonGroup>

      <br />
      <h1>View Captured Audio</h1>
      <Lowlight language="json" value={JSON.stringify(result.data, null, 2)} markers={[]} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const ButtonGroup = styled(Flex)`
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;
